import MockAdapter from 'axios-mock-adapter';
import { featureProvider } from '@root/featuresToggles';
import ENV from '@/constants/env';
import { USER_TYPES, USER_TYPE_ID_NAMES } from '@/constants';
import { HTTP_CODES } from '@/constants/http';
import {
  STUDENT_ENDPOINT,
  INSTRUCTOR_ENDPOINT,
  LEGEND_ENDPOINT,
  CALENDAR_ENDPOINT,
  LESSONS_HISTORY_ENDPOINT,
  RATE_HISTORY_ENDPOINT,
  STUDENTS_HISTORY_ENDPOINT,
  RESERVE_LESSON_ENDPOINT,
  REFUSE_LESSON_ENDPOINT,
  BLOCK_LESSON_ENDPOINT,
  BLOCK_CASHING_ENDPOINT,
  UNBLOCK_CASHING_ENDPOINT,
  CANCEL_EVENT_ENDPOINT,
  MODAL_QUALITY_RATING_ENDPOINT,
  MODAL_SEND_QUALITY_RATING_ENDPOINT,
  REFUSE_LESSON_BY_ADMIN_ENDPOINT,
  RESERVE_LESSON_BY_ADMIN_ENDPOINT,
  MODAL_UPDATE_RATING_ENDPOINT,
} from '@/api/constants';
import { randomInteger } from '@/utils';
import logger from '@/utils/logger';
import {
  studentMapper,
  instructorMapper,
  legendMapper,
  calendarMapper,
  historyMapper,
  rateHistoryMapper,
  qualityRatingMapper,
} from '@/api/mappers';
import factoryHttp from '@/utils/factoryHttp';

const API_ENDPOINT = ENV.API_BASE_URL;
const MOCKS_ENDPOINT = ENV.MOCK_BASE_URL;

const http = factoryHttp({
  baseURL: API_ENDPOINT,
});

// mocks:
if (featureProvider.enabled('MOCKED_API')) {
  if (featureProvider.enabled('MOCKED_API_LOCAL_SERVER')) {
    logger.log('api', 'Mock API local server enabled');

    // new mocks
    const mock = new MockAdapter(http.getInstance(), {
      delayResponse: randomInteger(200, 2000),
    });
    const httpMock = factoryHttp({
      baseURL: MOCKS_ENDPOINT,
    });

    mock.onGet(STUDENT_ENDPOINT).reply(async (config) => {
      const response = await httpMock.get('/users');
      const dataMock = {};

      // eslint-disable-next-line
      console.log('config:', config);
      // eslint-disable-next-line
      console.log('response:', response);

      // const { default: dataMock } = await import('@mocks/student.json');
      return [HTTP_CODES.OK, dataMock];
    });

    mock.onAny().passThrough();
  } else {
    logger.log('api', 'Mock API enabled');

    // legacy mocks
    const mock = new MockAdapter(http.getInstance(), {
      delayResponse: randomInteger(200, 2000),
    });

    mock.onGet(STUDENT_ENDPOINT).reply(async () => {
      const { default: dataMock } = await import('@mocks/student.json');
      return [HTTP_CODES.OK, dataMock];
    });

    mock.onGet(INSTRUCTOR_ENDPOINT).reply(async () => {
      const { default: dataMock } = await import('@mocks/instructor.json');
      return [HTTP_CODES.OK, dataMock];
    });

    mock.onGet(LEGEND_ENDPOINT).reply(async () => {
      const { default: dataMock } = await import('@mocks/legend.json');
      return [HTTP_CODES.OK, dataMock];
    });

    mock.onGet(CALENDAR_ENDPOINT).reply(async ({ params }) => {
      const [userType] = Object.entries(USER_TYPE_ID_NAMES).find(([, idName]) => params[idName]);
      const { default: calendarAdminInfoInstructorMock } = await import('@mocks/calendarAdminInfoInstructor.json');
      const { default: calendarAdminInstructorMock } = await import('@mocks/calendarAdminInstructor.json');
      const { default: calendarAdminStudentMock } = await import('@mocks/calendarAdminStudent.json');
      const { default: calendarInstructorMock } = await import('@mocks/calendarInstructor.json');
      const { default: calendarStudentMock } = await import('@mocks/calendarStudent.json');

      const getData = (key) => ({
        [USER_TYPES.ADMIN_INFO_INSTRUCTOR]: calendarAdminInfoInstructorMock,
        [USER_TYPES.ADMIN_INSTRUCTOR]: calendarAdminInstructorMock,
        [USER_TYPES.ADMIN_STUDENT]: calendarAdminStudentMock,
        [USER_TYPES.INSTRUCTOR]: calendarInstructorMock,
        [USER_TYPES.STUDENT]: calendarStudentMock,
        [USER_TYPES.REEGISTRATION_STUDENT]: calendarStudentMock,
      }[key]);

      return [HTTP_CODES.OK, getData(userType)];
    });

    mock.onGet(LESSONS_HISTORY_ENDPOINT).reply(async () => {
      const { default: dataMock } = await import('@mocks/lessonsHistory.json');
      return [HTTP_CODES.OK, dataMock];
    });

    mock.onGet(RATE_HISTORY_ENDPOINT).reply(async () => {
      const { default: dataMock } = await import('@mocks/rateHistory.json');
      return [HTTP_CODES.OK, dataMock];
    });

    mock.onGet(STUDENTS_HISTORY_ENDPOINT).reply(async () => {
      const { default: dataMock } = await import('@mocks/studentsHistory.json');
      return [HTTP_CODES.OK, dataMock];
    });

    mock.onPut(RESERVE_LESSON_ENDPOINT).reply(async (config) => {
      const userType = USER_TYPE_ID_NAMES[USER_TYPES.STUDENT];
      const data = JSON.parse(config.data);
      const query = {
        [userType]: data[userType],
        filter: data.filter,
      };
      const response = await http.get(CALENDAR_ENDPOINT, query);

      return [HTTP_CODES.OK, response];
    });

    mock.onPut(REFUSE_LESSON_ENDPOINT).reply(async (config) => {
      const userType = USER_TYPE_ID_NAMES[USER_TYPES.STUDENT];
      const data = JSON.parse(config.data);
      const query = {
        [userType]: data[userType],
        filter: data.filter,
      };
      const response = await http.get(CALENDAR_ENDPOINT, query);

      return [HTTP_CODES.OK, response];
    });

    mock.onPut(BLOCK_LESSON_ENDPOINT).reply(async (config) => {
      logger.log('blockLesson()', 'config:', config);
      return [HTTP_CODES.OK];
    });

    mock.onPut(BLOCK_CASHING_ENDPOINT).reply(async (config) => {
      logger.log('blockCashing()', 'config:', config);
      return [HTTP_CODES.OK];
    });

    mock.onPut(UNBLOCK_CASHING_ENDPOINT).reply(async (config) => {
      logger.log('unblockCashing()', 'config:', config);
      return [HTTP_CODES.OK];
    });

    mock.onPut(MODAL_UPDATE_RATING_ENDPOINT).reply(async (config) => {
      logger.log('blockCashing()', 'config:', config);
      return [HTTP_CODES.OK];
    });

    mock.onPut(CANCEL_EVENT_ENDPOINT).reply(async (config) => {
      logger.log('cancelEvent()', 'config:', config);
      return [HTTP_CODES.OK];
    });

    mock.onGet(MODAL_QUALITY_RATING_ENDPOINT).reply(async () => {
      const { default: dataMock } = await import('@mocks/modalQualityRating.json');
      return [HTTP_CODES.OK, dataMock];
    });

    mock.onPut(MODAL_SEND_QUALITY_RATING_ENDPOINT).reply(async () => {
      const { default: dataMock } = await import('@mocks/modalSendQualityRating.json');
      return [HTTP_CODES.OK, dataMock];
    });

    mock.onAny().passThrough();
  }
}
// mocks;

export default {
  /**
   * @param {String} studentId
   * @returns {Promise<Object>}
   */
  async getStudent(studentId) {
    const query = { studentId };
    const config = { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } };
    const response = await http.get(STUDENT_ENDPOINT, query, config);
    const { data } = response;

    if (!data) {
      logger.error('getStudent: property "data" not found');
      return false;
    }

    return studentMapper(data);
  },

  /**
   * @param {String} instructorId
   * @returns {Promise<Object>}
   */
  async getInstructor(instructorId) {
    const query = { instructorId };
    const config = { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } };
    const response = await http.get(INSTRUCTOR_ENDPOINT, query, config);
    const { data } = response;

    if (!data) {
      logger.error('getInstructor: property "data" not found');
      return false;
    }

    return instructorMapper(data);
  },

  /**
   * @returns {Promise<Array>}
   */
  async getLegend() {
    const config = { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } };
    const response = await http.get(LEGEND_ENDPOINT, null, config);
    const { data } = response;

    if (!data) {
      logger.error('getLegend: property "data" not found');
      return false;
    }

    return legendMapper(data);
  },

  /**
   * @param {String} userId
   * @param appUser
   * @param {String} userType
   * @param {Object} filter
   * @returns {Promise<Object>}
   */
  async getCalendar(userId, appUser, userType, filter = {}) {
    const {
      currentPage = 1,
      perPage = 7,
    } = filter;
    const query = {
      [USER_TYPE_ID_NAMES[userType]]: userId,
      appuser: appUser,
      filter: { currentPage, perPage },
    };
    const config = { headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' } };
    const response = await http.get(CALENDAR_ENDPOINT, query, config);
    const { data, meta } = response;

    if (!data || !meta) {
      logger.error('getCalendar: properties "data" or "meta" not found');
      return false;
    }

    return {
      data: calendarMapper(userType, data),
      meta,
    };
  },

  /**
   * @param {String} studentId
   * @param {Number} page
   * @returns {Promise<Object>}
   */
  async getLessonsHistory(studentId, page = 1) {
    const query = { studentId, page };
    const response = await http.get(LESSONS_HISTORY_ENDPOINT, query);
    const { data, meta } = response;

    if (!data) {
      logger.error('getLessonsHistory: property "data" not found');
      return false;
    }

    return {
      data: historyMapper(data),
      meta,
    };
  },

  /**
   * @param {String} instructorId
   * @param {Number} page
   * @returns {Promise<Object>}
   */
  async getRateHistory(instructorId, page = 1) {
    const query = { instructorId, page };
    const response = await http.get(RATE_HISTORY_ENDPOINT, query);
    const { data, meta } = response;

    if (!data) {
      logger.error('getRateHistory: property "data" not found');
      return false;
    }

    return {
      data: rateHistoryMapper(data),
      meta,
    };
  },

  /**
   * @param {String} instructorId
   * @param {Number} page
   * @returns {Promise<Object>}
   */
  async getStudentsHistory(instructorId, page = 1) {
    const query = { instructorId, page };
    const response = await http.get(STUDENTS_HISTORY_ENDPOINT, query);
    const { data, meta } = response;

    if (!data) {
      logger.error('getStudentsHistory: property "data" not found');
      return false;
    }

    return { data, meta };
  },

  /**
   * @param {{ studentId: String, date: String, time: String, filter: Object }} payload
   * @returns {Promise<Object>}
   */
  async reserveLesson(payload) {
    const {
      studentId,
      date,
      time,
      filter,
    } = payload;
    const query = {
      studentId,
      date,
      time: `${time}:00`,
      filter,
    };
    const response = await http.put(RESERVE_LESSON_ENDPOINT, query);
    return this.responseHelper('reserveLesson', USER_TYPES.STUDENT, response);
  },

  /**
   * @param {{ studentId: String, date: String, time: String, filter: Object }} payload
   * @returns {Promise<Object>}
   */
  async reserveLessonByAdmin(payload) {
    const {
      studentId,
      date,
      time,
      filter,
    } = payload;
    const query = {
      studentId,
      date,
      time: `${time}:00`,
      filter,
    };
    const response = await http.put(RESERVE_LESSON_BY_ADMIN_ENDPOINT, query);
    return this.responseHelper('reserveLesson', USER_TYPES.STUDENT, response);
  },

  /**
   * @param {{ studentId: String, date: String, time: String, filter: Object }} payload
   * @returns {Promise<Object>}
   */
  async refuseLesson(payload) {
    const {
      studentId,
      date,
      time,
      filter,
    } = payload;
    const query = {
      studentId,
      date,
      time: `${time}:00`,
      filter,
    };
    const response = await http.put(REFUSE_LESSON_ENDPOINT, query);
    return this.responseHelper('refuseLesson', USER_TYPES.STUDENT, response);
  },
  /**
   * @param {{ studentId: String, date: String, time: String, filter: Object }} payload
   * @returns {Promise<Object>}
   */
  async refuseLessonByAdmin(payload) {
    const {
      studentId,
      date,
      time,
      filter,
    } = payload;
    const query = {
      studentId,
      date,
      time: `${time}:00`,
      filter,
    };
    const response = await http.put(REFUSE_LESSON_BY_ADMIN_ENDPOINT, query);
    return this.responseHelper('refuseLessonByAdmin', USER_TYPES.STUDENT, response);
  },
  /**
   * @param {{ instructorId: String, date: String, time: String, filter: Object }} payload
   * @returns {Promise<Object>}
   */
  async blockLesson(payload) {
    const {
      instructorId,
      date,
      time,
      filter,
    } = payload;
    const query = {
      instructorId,
      date,
      time: `${time}:00`,
      filter,
    };
    const response = await http.put(BLOCK_LESSON_ENDPOINT, query);
    return this.responseHelper('blockLesson', USER_TYPES.ADMIN_INSTRUCTOR, response);
  },

  /**
   * @param {{ instructorId: String, date: String, time: String, filter: Object }} payload
   * @returns {Promise<Object>}
   */
  async blockCashing(payload) {
    const {
      instructorId,
      date,
      time,
      appuser,
      filter,
    } = payload;
    const query = {
      instructorId,
      date,
      appuser,
      time: `${time}:00`,
      filter,
    };
    const response = await http.put(BLOCK_CASHING_ENDPOINT, query);
    return this.responseHelper('blockCashing', USER_TYPES.ADMIN_INFO_INSTRUCTOR, response);
  },

  /**
   * @param {{ instructorId: String, date: String, time: String, filter: Object }} payload
   * @returns {Promise<Object>}
   */
  async unblockCashing(payload) {
    const {
      instructorId,
      date,
      time,
      filter,
      appuser,
    } = payload;
    const query = {
      instructorId,
      appuser,
      date,
      time: `${time}:00`,
      filter,
    };
    const response = await http.put(UNBLOCK_CASHING_ENDPOINT, query);
    return this.responseHelper('unblockCashing', USER_TYPES.ADMIN_INFO_INSTRUCTOR, response);
  },

  /**
   * @param {{ instructorId: String, date: String, time: String, filter: Object }} payload
   * @returns {Promise<Object>}
   */
  async cancelEvent(payload) {
    const {
      instructorId,
      date,
      time,
      filter,
    } = payload;
    const query = {
      instructorId,
      date,
      time: `${time}:00`,
      filter,
    };
    const response = await http.put(CANCEL_EVENT_ENDPOINT, query);
    return this.responseHelper('cancelEvent', USER_TYPES.INSTRUCTOR, response);
  },

  /**
   * @param {String} methodName
   * @param {String} userType
   * @param {Object} response
   * @returns {Object}
   */
  responseHelper(methodName, userType, response) {
    const { data, meta, errors } = response;

    if (errors?.length) {
      return { errors };
    }
    if (!data) {
      logger.error(`${methodName}: property "data" is not found`);
      return false;
    }

    return {
      data: calendarMapper(userType, data),
      meta,
      errors,
    };
  },

  /**
   * @param {String} studentId
   * @returns {Promise<Object>}
   */
  async getModalQualityRating(studentId) {
    const query = { studentId };
    const response = await http.get(MODAL_QUALITY_RATING_ENDPOINT, query);
    const { data } = response;

    if (!data) {
      return false;
    }

    return qualityRatingMapper(data);
  },

  /**
   * @param {Object} payload
   * @returns {Promise<boolean>}
   */
  async sendModalQualityRating(payload) {
    const query = { ...payload };
    const response = await http.put(MODAL_SEND_QUALITY_RATING_ENDPOINT, query);
    const { data } = response;

    if (!data) {
      return false;
    }

    return data;
  },

  /**
   * @param {Object} payload
   * @returns {Promise<boolean>}
   */
  async updateRating(payload) {
    const {
      instructorId,
      date,
      time,
      rating,
      filter,
      appuser,
    } = payload;
    const query = {
      instructorId,
      appuser,
      rating,
      date: `${date} ${time}`,
      filter,
    };
    const response = await http.put(MODAL_UPDATE_RATING_ENDPOINT, query);
    return this.responseHelper('updateRating', USER_TYPES.ADMIN_INFO_INSTRUCTOR, response);
  },
};
